import React, { useState } from 'react';
import { X, Star, Gift, Send } from 'lucide-react';

const FeedbackModal = ({ isOpen, onClose, onSubmit, assessmentsRemaining, userEmail }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState('');
  const [mostValuable, setMostValuable] = useState('');
  const [improvements, setImprovements] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      alert('Please provide a rating');
      return;
    }

    setIsSubmitting(true);

    const feedbackData = {
      email: userEmail,
      rating,
      feedback,
      wouldRecommend,
      mostValuable,
      improvements,
      timestamp: new Date().toISOString(),
      assessmentsRemaining
    };

    try {
      await onSubmit(feedbackData);
      // Reset form
      setRating(0);
      setFeedback('');
      setWouldRecommend('');
      setMostValuable('');
      setImprovements('');
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Gift size={28} />
                <h2 className="text-2xl font-bold">Earn 5 Free Assessments! 🎉</h2>
              </div>
              <p className="text-teal-50">
                You have {assessmentsRemaining} assessment{assessmentsRemaining !== 1 ? 's' : ''} remaining. 
                Share your feedback and get 5 bonus assessments!
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 p-1"
              title="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rating */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              How would you rate CliniScore? *
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-3 text-lg font-semibold text-gray-700 self-center">
                  {rating} / 5
                </span>
              )}
            </div>
          </div>

          {/* Overall Feedback */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              What do you think of CliniScore?
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share your overall thoughts..."
              className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Would Recommend */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Would you recommend CliniScore to colleagues?
            </label>
            <div className="flex gap-3">
              {['Yes', 'Maybe', 'No'].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setWouldRecommend(option)}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-colors ${
                    wouldRecommend === option
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Most Valuable Feature */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              What's the most valuable feature?
            </label>
            <textarea
              value={mostValuable}
              onChange={(e) => setMostValuable(e.target.value)}
              placeholder="e.g., Speed of assessment generation, accuracy of scores, justifications..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Improvements */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              What could we improve?
            </label>
            <textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder="Any features you'd like to see or issues you've encountered..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none resize-none"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Maybe Later
            </button>
            <button
              type="submit"
              disabled={isSubmitting || rating === 0}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                isSubmitting || rating === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Submit & Get 5 Free Assessments
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            * Required field. Your feedback helps us improve CliniScore for everyone.
          </p>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;

